import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";

import PageBanner from "../../components/PageBanner";
import { fetchPatientQuestionnaires, submitPatientQuestionnaire } from "../../services/patientQuestionnaireService";
import { normalizeQuestionSchemaForBackend } from "../questionnaire-management/questionnaireSchemaUtils";

import "survey-core/defaultV2.min.css";
import "./patientSurveyOverrides.css";

const PatientQuestionnaire = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { executionId, scheduleId } = useParams();

  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState([]);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchPatientQuestionnaires({
          executionId,
          scheduleId,
        });
        if (!cancelled) {
          setSteps(Array.isArray(data) ? data : []);
          setActiveStepIndex(0);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load questionnaires.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [executionId, scheduleId]);

  const activeStep = steps[activeStepIndex] || null;
  const [surveyModel, setSurveyModel] = useState(null);

  useEffect(() => {
    if (!activeStep || !activeStep.schema) {
      setSurveyModel(null);
      return;
    }

    try {
      let rawSchema = activeStep.schema;

      // Some older questionnaires may have schema stored as JSON string.
      if (typeof rawSchema === "string") {
        try {
          rawSchema = JSON.parse(rawSchema);
        } catch (e) {
          console.warn("[PatientQuestionnaire] Failed to parse schema JSON string:", e);
          setSurveyModel(null);
          return;
        }
      }

      // Normalize schema to ensure valid SurveyJS structure (pages[].elements, slider flags, etc.)
      const normalizedSchema = normalizeQuestionSchemaForBackend(rawSchema || {});
      const model = new Model(normalizedSchema);

      if (activeStep.submitted_answers && typeof activeStep.submitted_answers === "object") {
        model.data = activeStep.submitted_answers;
      }

      const statusLower = String(activeStep.status || "").toLowerCase();
      let cleanup = undefined;

      if (statusLower === "completed") {
        // Read-only mode if already completed – cannot fill again.
        model.mode = "display";
      } else {
        // Wire up save on SurveyJS complete button.
        const onCompleteHandler = async (sender) => {
          // Guard against double-complete if step status changed meanwhile.
          const currentStatus = String(activeStep.status || "").toLowerCase();
          if (currentStatus === "completed") {
            return;
          }

          setSaving(true);
          setError("");
          setSuccessMessage("");
          try {
            const resultData = sender.data || {};
            await submitPatientQuestionnaire({
              executionId,
              scheduleId,
              qid: activeStep.qid,
              answersJson: resultData,
            });

            // Compute next step index (first non-completed step after current).
            const currentIndex = steps.findIndex((step) => step.qid === activeStep.qid);
            let nextIndex = -1;
            for (let i = currentIndex + 1; i < steps.length; i += 1) {
              const s = steps[i];
              if (String(s.status || "").toLowerCase() !== "completed") {
                nextIndex = i;
                break;
              }
            }

            setSteps((prev) =>
              prev.map((step) =>
                step.qid === activeStep.qid
                  ? {
                      ...step,
                      status: "completed",
                      submitted_answers: resultData,
                    }
                  : step
              )
            );
            setSuccessMessage("Your answers have been saved for this step.");
            // Lock this survey after successful completion.
            sender.mode = "display";

            // Move to next pending step if available.
            if (nextIndex !== -1) {
              setActiveStepIndex(nextIndex);
            }
          } catch (err) {
            setError(err?.message || "Failed to save answers.");
          } finally {
            setSaving(false);
          }
        };

        model.onComplete.add(onCompleteHandler);
        cleanup = () => {
          model.onComplete.remove(onCompleteHandler);
        };
      }

      setSurveyModel(model);
      return cleanup;
    } catch (e) {
      setSurveyModel(null);
    }
  }, [activeStep, executionId, scheduleId]);

  const handleStepChange = (index) => {
    if (index < 0 || index >= steps.length) return;
    setActiveStepIndex(index);
    setSuccessMessage("");
    setError("");
  };

  const handleSubmit = async () => {
    if (!activeStep || !surveyModel) return;
    setSaving(true);
    setError("");
    setSuccessMessage("");
    try {
      const resultData = surveyModel.data || {};
      await submitPatientQuestionnaire({
        executionId,
        scheduleId,
        qid: activeStep.qid,
        answersJson: resultData,
      });

      setSteps((prev) =>
        prev.map((step) =>
          step.qid === activeStep.qid
            ? {
                ...step,
                status: "completed",
                submitted_answers: resultData,
              }
            : step
        )
      );
      setSuccessMessage("Your answers have been saved for this step.");
    } catch (err) {
      setError(err?.message || "Failed to save answers.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          bgcolor: "#c7d7df",
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading questionnaires…</Typography>
      </Box>
    );
  }

  if (!steps.length) {
    return (
      <Box className="patient-questionnaire-page" sx={{ bgcolor: "#c7d7df", minHeight: "100vh" }}>
        <PageBanner title="Questionnaires" />
        <Container sx={{ pt: 4, px: { xs: 2, sm: 3 } }}>
          <Typography>
            There are no questionnaires configured for this visit at the moment.
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="patient-questionnaire-page" sx={{ bgcolor: "#c7d7df", minHeight: "100vh" }}>
      <PageBanner title="Questionnaires" />

      <Container maxWidth="lg" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
        <Stepper
          nonLinear
          activeStep={activeStepIndex}
          alternativeLabel={!isMobile}
          orientation={isMobile ? "vertical" : "horizontal"}
          sx={{ overflowX: isMobile ? "visible" : "auto" }}
        >
          {steps.map((step, index) => (
            <Step key={step.qid} completed={String(step.status).toLowerCase() === "completed"}>
              <StepButton color="inherit" onClick={() => handleStepChange(index)}>
                <StepLabel>
                  <Typography variant="body2">
                    Step {step.step}: {step.label || step.name || step.qid}
                  </Typography>
                </StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>

        <Box
          sx={{
            mt: 3,
            p: { xs: 2, sm: 3, md: 4 },
            bgcolor: "#ffffff",
            borderRadius: 2,
            boxShadow: 1,
            overflowX: "auto",
          }}
        >
          {activeStep && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Step {activeStep.step}: {activeStep.label || activeStep.name || activeStep.qid}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                Please fill the form below and click the Complete button at the end of the form.
              </Typography>

              {surveyModel ? (
                <Box className="patient-survey-runtime" sx={{ mb: 3 }}>
                  <Survey model={surveyModel} />
                </Box>
              ) : (
                <Typography color="error">
                  Unable to render this questionnaire. Please contact support.
                </Typography>
              )}

              {error && (
                <Typography color="error" sx={{ mb: 1 }}>
                  {error}
                </Typography>
              )}
              {successMessage && (
                <Typography color="success.main" sx={{ mb: 1 }}>
                  {successMessage}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PatientQuestionnaire;
